import type { Crm } from "../lib/crm";
import { useAsync } from "../lib/useAsync";
import { fmtDateTime, humanise } from "../lib/format";
import type { TimelineEntry } from "../lib/types";
import { Async, Empty } from "./ui";
import { beforeOgmaLabel } from "../lib/history";

const KIND_LABEL: Record<TimelineEntry["kind"], string> = {
  message: "Reply received",
  touch: "Sent",
  note: "Note",
  meeting: "Meeting",
  signal: "Signal",
  event: "Stage changed",
};

/** What happened. A module hand-off (one owner at a time, migration 20261109090000) is an event of its own. */
export function kindLabel(e: TimelineEntry): string {
  if (e.kind === "event" && e.title === "module.handoff") return "Module hand-off";
  // 10.19 their answer to "How did we do?" (the body says happy, okay or unhappy, and their comment)
  if (e.kind === "event" && e.title === "happiness.answered") return "How did we do?";
  // Past conversations imported from before Ogma (0.28, 0.40-0.42): "Before Ogma · HubSpot".
  const before = (e.kind === "message" || e.kind === "touch") ? beforeOgmaLabel(e.title) : null;
  if (before) return `${before}${e.kind === "message" ? " · from them" : ""}`;
  // A message the business sent from the WhatsApp Business app (coexistence) is ours, not a reply.
  if (e.kind === "message" && e.direction === "out") return "Sent";
  return KIND_LABEL[e.kind];
}

/** The bold line for an entry. Notes show who wrote them; stage changes need no title of their own. */
function titleOf(e: TimelineEntry): string | null {
  if (!e.title) return null;
  if (beforeOgmaLabel(e.title)) return null;          // the label says it; the body is the short summary
  if (e.kind === "event") return null;
  if (e.kind === "note") return null;
  if (e.kind === "meeting") return `Meeting ${humanise(e.title).toLowerCase()}`;
  if (e.kind === "message") return e.title === "unclassified" ? "Not sorted yet" : humanise(e.title);
  return humanise(e.title);
}

function byline(e: TimelineEntry): string | null {
  if (e.kind !== "note" || !e.title) return null;
  const who = e.title.replace(/^human:/, "");
  return who.endsWith("-agent") ? humanise(who) : `by ${who}`;
}

/** Who acted, for the notch colour on the timeline's stem: Ogma (the engine and its agents), you, or the contact. */
export function actorOf(e: TimelineEntry): "ogma" | "you" | "contact" {
  if (e.kind === "message") return e.direction === "out" ? "ogma" : "contact";
  if (e.kind === "touch" && beforeOgmaLabel(e.title)) return "you";     // sent by the business before Ogma
  if (e.kind === "meeting") return "you";
  if (e.kind === "event" && e.title === "happiness.answered") return "contact";
  if (e.kind === "note") return e.title && !e.title.endsWith("-agent") ? "you" : "ogma";
  return "ogma";
}

const ACTOR_WORDS = { ogma: "Ogma", you: "You", contact: "Them" } as const;

/** A firm's timeline, or one person's when `person` is given (crm_timeline). */
export function Timeline({ crm, organisation, person, limit = 100, version = 0 }: { crm: Crm; organisation?: string; person?: string; limit?: number; version?: number }) {
  const items = useAsync(
    () => (person ? crm.personTimeline(person, limit) : organisation ? crm.timeline(organisation, limit) : Promise.resolve([] as TimelineEntry[])),
    [crm, organisation, person, limit, version],
  );
  const who = person ? "this person" : "this firm";
  return (
    <Async state={items} label="Loading the timeline" empty={(d) => (d.length === 0 ? <Empty title={`Nothing has happened with ${who} yet`}>Messages, replies, meetings and notes appear here as they happen.</Empty> : null)}>
      {(entries) => (
        <ol className="timeline">
          {entries.map((e) => (
            <li key={`${e.kind}-${e.ref_id}`} className={`tl tl-${e.kind} tl-by-${actorOf(e)}`}>
              <div className="tl-meta" title={e.step_id ? `Step ${e.step_id}` : undefined}>
                <span className="tl-who">{ACTOR_WORDS[actorOf(e)]}</span>
                <time dateTime={e.at}>{fmtDateTime(e.at)}</time>
                <span>
                  {kindLabel(e)}
                  {e.channel ? ` by ${e.channel}` : ""}
                  {byline(e) ? ` ${byline(e)}` : ""}
                </span>
              </div>
              <div className="tl-body">
                {titleOf(e) && <strong>{titleOf(e)}</strong>}
                {e.body && <p className="pre">{e.body}</p>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Async>
  );
}
